"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { X, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatusBanner, StatusBannerRow, StatusBannerContent, StatusBannerActions, StatusBannerIconButton } from "@/components/ui/status-banner";
import { useUpgradeToPro } from "@/entities/addon";
import { isAddonFeature } from "@/domain/features";
import { usePublishBannerHeight } from "@/shared/ui/use-publish-banner-height";
import { useAddonRequestBanner } from "../model/use-addon-request-banner";

/**
 * Solid purple banner for org admins showing pending add-on access requests.
 *
 * The add-on purchase flow is retired (spec §10.4): "View details" now routes
 * to Upgrade-to-Pro (Pro bundles both add-ons and grants every Base member
 * access) instead of the old add-on activation modal.
 *
 * Three display states:
 * 1. Single request: "[Name] has requested access to [Addon]" with View details
 * 2. Multiple collapsed: "You have X pending add-on requests for [A], [B]" with View details
 * 3. Expanded: Per-addon rows with requester names, clickable to Upgrade-to-Pro
 */
export function AdminRequestBanner() {
  const $ = _c(70);
  const {
    summaries,
    totalCount,
    requestersByAddon,
    hasRequests,
    isLoading,
    showConfirmModal,
    openConfirmModal,
    closeConfirmModal,
    acknowledgeAll,
    isAcknowledging
  } = useAddonRequestBanner();
  const upgradeToPro = useUpgradeToPro();
  const [isExpanded, setIsExpanded] = useState(false);
  const shown = hasRequests && !isLoading;
  const hostRef = usePublishBannerHeight("admin");
  if (!shown) {
    return null;
  }
  const isSingleRequest = totalCount === 1 && summaries.length === 1;
  let t0;
  if ($[0] !== isSingleRequest || $[1] !== requestersByAddon || $[2] !== summaries[0]) {
    t0 = isSingleRequest ? requestersByAddon.get(summaries[0].addonKey)?.[0] : null;
    $[0] = isSingleRequest;
    $[1] = requestersByAddon;
    $[2] = summaries[0];
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const singleRequesterName = t0;
  let t1;
  if ($[4] !== upgradeToPro) {
    t1 = addonKey => {
      if (isAddonFeature(addonKey)) {
        upgradeToPro();
      }
    };
    $[4] = upgradeToPro;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleViewDetails = t1;
  let t2;
  if ($[6] !== handleViewDetails || $[7] !== isSingleRequest || $[8] !== openConfirmModal || $[9] !== singleRequesterName || $[10] !== summaries[0]) {
    t2 = isSingleRequest && <StatusBannerRow><StatusBannerContent><p className="text-sm text-white"><span className="font-medium">{singleRequesterName ?? "A team member"}</span>{" has requested access to "}<span className="font-medium">{summaries[0].addonName}</span></p></StatusBannerContent><StatusBannerActions><Button variant="outline" size="sm" className="h-7 border-white/30 bg-white/20 text-xs text-white backdrop-blur-xs hover:bg-white/30" onClick={() => handleViewDetails(summaries[0].addonKey)}>View details</Button><StatusBannerIconButton color="purple" onClick={openConfirmModal} aria-label="Dismiss"><X className="h-4 w-4" /></StatusBannerIconButton></StatusBannerActions></StatusBannerRow>;
    $[6] = handleViewDetails;
    $[7] = isSingleRequest;
    $[8] = openConfirmModal;
    $[9] = singleRequesterName;
    $[10] = summaries[0];
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== isExpanded || $[13] !== isSingleRequest || $[14] !== openConfirmModal || $[15] !== summaries || $[16] !== totalCount) {
    t3 = !isSingleRequest && !isExpanded && <StatusBannerRow><StatusBannerContent><p className="text-sm text-white">{"You have "}<span className="font-medium">{totalCount} pending</span>{" add-on requests for "}<span className="font-medium">{summaries.map(_temp).join(", ")}</span></p></StatusBannerContent><StatusBannerActions><Button variant="outline" size="sm" className="h-7 border-white/30 bg-white/20 text-xs text-white backdrop-blur-xs hover:bg-white/30" onClick={() => setIsExpanded(true)}>View details<ChevronDown className="ml-1 h-3 w-3" /></Button><StatusBannerIconButton color="purple" onClick={openConfirmModal} aria-label="Dismiss"><X className="h-4 w-4" /></StatusBannerIconButton></StatusBannerActions></StatusBannerRow>;
    $[12] = isExpanded;
    $[13] = isSingleRequest;
    $[14] = openConfirmModal;
    $[15] = summaries;
    $[16] = totalCount;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== handleViewDetails || $[19] !== isExpanded || $[20] !== isSingleRequest || $[21] !== openConfirmModal || $[22] !== requestersByAddon || $[23] !== summaries || $[24] !== totalCount) {
    t4 = !isSingleRequest && isExpanded && <div><StatusBannerRow className="pb-2"><StatusBannerContent><p className="text-sm font-medium text-white">{totalCount} pending add-on requests</p></StatusBannerContent><StatusBannerActions><Button variant="outline" size="sm" className="h-7 border-white/30 bg-white/20 text-xs text-white backdrop-blur-xs hover:bg-white/30" onClick={() => setIsExpanded(false)}>Collapse<ChevronUp className="ml-1 h-3 w-3" /></Button><StatusBannerIconButton color="purple" onClick={openConfirmModal} aria-label="Dismiss"><X className="h-4 w-4" /></StatusBannerIconButton></StatusBannerActions></StatusBannerRow><div className="border-t border-white/20">{summaries.map(summary => {
          const requesters = requestersByAddon.get(summary.addonKey) ?? [];
          return <button key={summary.addonKey} type="button" onClick={() => handleViewDetails(summary.addonKey)} className="flex w-full items-center justify-between gap-3 border-b border-white/20 px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-white/10"><div className="min-w-0"><p className="text-sm font-medium text-white">{summary.addonName}<span className="ml-1.5 font-normal text-white/70">— {summary.pendingCount} {summary.pendingCount === 1 ? "request" : "requests"}</span></p>{requesters.length > 0 && <p className="mt-0.5 truncate text-xs text-white/70">Requested by {requesters.join(", ")}</p>}</div><ChevronRight className="h-4 w-4 shrink-0 text-white/70" /></button>;
        })}</div></div>;
    $[18] = handleViewDetails;
    $[19] = isExpanded;
    $[20] = isSingleRequest;
    $[21] = openConfirmModal;
    $[22] = requestersByAddon;
    $[23] = summaries;
    $[24] = totalCount;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t2 || $[27] !== t3 || $[28] !== t4) {
    t5 = <StatusBanner color="purple">{t2}{t3}{t4}</StatusBanner>;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== hostRef || $[31] !== t5) {
    t6 = <div ref={hostRef} className="flow-root">{t5}</div>;
    $[30] = hostRef;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== closeConfirmModal) {
    t7 = open => !open && closeConfirmModal();
    $[33] = closeConfirmModal;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10"><X className="h-6 w-6 text-destructive" /></div>;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const t9 = totalCount === 1 ? "this request" : `${totalCount} requests`;
  let t10;
  if ($[36] !== t9) {
    t10 = <AlertDialogTitle className="text-center">Dismiss {t9}?</AlertDialogTitle>;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const t11 = totalCount === 1 ? "Your team member is waiting for a response." : `${totalCount} team members are waiting for a response.`;
  let t12;
  if ($[38] !== t11) {
    t12 = <AlertDialogDescription className="text-center">{t11}{" "}Dismissing will remove these requests from your banner without taking action.</AlertDialogDescription>;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t10 || $[41] !== t12) {
    t13 = <AlertDialogHeader>{t8}{t10}{t12}</AlertDialogHeader>;
    $[40] = t10;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== requestersByAddon || $[44] !== summaries) {
    let t15;
    if ($[46] !== requestersByAddon) {
      t15 = summary_0 => {
        const requesters_0 = requestersByAddon.get(summary_0.addonKey) ?? [];
        return <div key={summary_0.addonKey} className="rounded-md px-3 py-2"><div className="flex items-center justify-between"><span className="text-sm font-medium">{summary_0.addonName}</span><span className="text-xs text-muted-foreground">{summary_0.pendingCount} {summary_0.pendingCount === 1 ? "request" : "requests"}</span></div>{requesters_0.length > 0 && <p className="mt-0.5 text-xs text-muted-foreground">{requesters_0.join(", ")}</p>}</div>;
      };
      $[46] = requestersByAddon;
      $[47] = t15;
    } else {
      t15 = $[47];
    }
    t14 = summaries.map(t15);
    $[43] = requestersByAddon;
    $[44] = summaries;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[48] !== t14) {
    t15 = <div className="my-1 max-h-48 overflow-y-auto rounded-lg border bg-muted/50 p-1">{t14}</div>;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== isAcknowledging) {
    t16 = <AlertDialogCancel disabled={isAcknowledging} className="w-full bg-[#6252fd] text-white hover:bg-[#5244e0]">Keep requests</AlertDialogCancel>;
    $[50] = isAcknowledging;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const t17 = isAcknowledging ? "Dismissing..." : "Dismiss anyway";
  let t18;
  if ($[52] !== acknowledgeAll || $[53] !== isAcknowledging || $[54] !== t17) {
    t18 = <AlertDialogAction onClick={acknowledgeAll} disabled={isAcknowledging} className="w-full border border-input bg-background text-muted-foreground shadow-xs hover:bg-accent hover:text-accent-foreground">{t17}</AlertDialogAction>;
    $[52] = acknowledgeAll;
    $[53] = isAcknowledging;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t16 || $[57] !== t18) {
    t19 = <AlertDialogFooter className="sm:flex-col sm:gap-2 sm:space-x-0">{t16}{t18}</AlertDialogFooter>;
    $[56] = t16;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== t13 || $[60] !== t15 || $[61] !== t19) {
    t20 = <AlertDialogContent className="border-destructive/20 sm:max-w-md">{t13}{t15}{t19}</AlertDialogContent>;
    $[59] = t13;
    $[60] = t15;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== showConfirmModal || $[64] !== t20 || $[65] !== t7) {
    t21 = <AlertDialog open={showConfirmModal} onOpenChange={t7}>{t20}</AlertDialog>;
    $[63] = showConfirmModal;
    $[64] = t20;
    $[65] = t7;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== t21 || $[68] !== t6) {
    t22 = <>{t6}{t21}</>;
    $[67] = t21;
    $[68] = t6;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  return t22;
}
function _temp(s) {
  return s.addonName;
}